import React, { useState, useEffect, useRef } from 'react';
import { Badge } from './badge';
import { Input } from './input';
import { X, Search, Store, Check } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * MerchantComboSelect - A searchable multi-select component for merchants
 * 
 * Props:
 * - merchants: Array of merchant objects { id, name, code, platform_id }
 * - selectedMerchants: Array of selected merchant IDs
 * - onSelect: Callback when a merchant is selected
 * - onRemove: Callback when a merchant is removed
 * - onClearAll: Callback when all merchants are cleared
 * - disabled: Whether the component is disabled
 * - placeholder: Placeholder text
 * - readOnly: If true, shows badges only (no edit capability)
 * - platforms: Array of platform objects for grouping { id, name }
 */
export const MerchantComboSelect = ({
  merchants = [],
  selectedMerchants = [],
  onSelect,
  onRemove,
  onClearAll,
  disabled = false,
  placeholder = "Search and select merchants...",
  readOnly = false,
  platforms = [],
  className
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter merchants based on search query
  const filteredMerchants = merchants.filter(merchant => {
    const searchLower = searchQuery.toLowerCase();
    return (
      merchant.name.toLowerCase().includes(searchLower) ||
      merchant.code.toLowerCase().includes(searchLower) ||
      merchant.id.toLowerCase().includes(searchLower)
    );
  });

  // Group merchants by platform
  const groupedMerchants = filteredMerchants.reduce((acc, merchant) => {
    const platformId = merchant.platform_id || 'other';
    if (!acc[platformId]) {
      acc[platformId] = [];
    }
    acc[platformId].push(merchant);
    return acc;
  }, {});

  // Get selected merchant objects
  const selectedMerchantObjects = selectedMerchants
    .map(id => merchants.find(m => m.id === id))
    .filter(Boolean);

  // Get platform name helper
  const getPlatformName = (platformId) => {
    const platform = platforms.find(p => p.id === platformId);
    return platform?.name || platformId;
  };

  const handleSelect = (merchantId) => {
    if (!selectedMerchants.includes(merchantId)) {
      onSelect(merchantId);
    }
    setSearchQuery('');
  };

  const handleRemove = (merchantId, e) => {
    e?.stopPropagation();
    onRemove(merchantId);
  };

  const handleClearAll = (e) => {
    e?.stopPropagation();
    if (onClearAll) {
      onClearAll();
    }
  };

  // Read-only mode - just show badges
  if (readOnly) {
    return (
      <div className={cn("flex flex-wrap gap-2", className)}>
        {selectedMerchantObjects.length > 0 ? (
          selectedMerchantObjects.map((merchant) => (
            <Badge 
              key={merchant.id} 
              variant="outline" 
              className="text-xs bg-slate-100 dark:bg-slate-800"
            >
              <Store className="h-3 w-3 mr-1" />
              {merchant.name}
              <span className="text-muted-foreground ml-1">({merchant.code})</span>
            </Badge>
          ))
        ) : (
          <span className="text-sm text-muted-foreground">No merchants assigned</span>
        )}
      </div>
    );
  }

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      {/* Selected merchants display */}
      {selectedMerchantObjects.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2 items-center">
          {selectedMerchantObjects.map((merchant) => (
            <Badge 
              key={merchant.id} 
              variant="secondary" 
              className="text-xs"
            >
              <Store className="h-3 w-3 mr-1" />
              {merchant.name}
              {!disabled && (
                <X 
                  className="h-3 w-3 ml-1 cursor-pointer hover:text-destructive" 
                  onClick={(e) => handleRemove(merchant.id, e)}
                />
              )}
            </Badge>
          ))}
          {!disabled && onClearAll && selectedMerchantObjects.length > 1 && (
            <button
              type="button"
              onClick={handleClearAll}
              className="text-xs text-muted-foreground hover:text-destructive underline"
            >
              Clear all
            </button>
          )}
        </div>
      )}

      {/* Search input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          ref={inputRef}
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onFocus={() => !disabled && setIsOpen(true)}
          placeholder={disabled ? "Select platforms first" : placeholder}
          disabled={disabled}
          className="pl-9"
          data-testid="merchant-search-input"
        />
      </div>

      {/* Dropdown */}
      {isOpen && !disabled && (
        <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-lg max-h-60 overflow-y-auto">
          {filteredMerchants.length === 0 ? (
            <div className="p-3 text-sm text-muted-foreground text-center">
              {searchQuery ? 'No merchants found' : 'No merchants available'}
            </div>
          ) : (
            Object.entries(groupedMerchants).map(([platformId, platformMerchants]) => (
              <div key={platformId}>
                {/* Platform header */}
                <div className="px-3 py-1.5 bg-muted/50 text-xs font-semibold text-muted-foreground sticky top-0">
                  {getPlatformName(platformId)}
                </div>
                {/* Merchants in platform */}
                {platformMerchants.map((merchant) => {
                  const isSelected = selectedMerchants.includes(merchant.id);
                  return (
                    <div
                      key={merchant.id}
                      onClick={() => !isSelected && handleSelect(merchant.id)}
                      className={cn(
                        "px-3 py-2 cursor-pointer flex items-center justify-between",
                        isSelected 
                          ? "bg-primary/10 text-primary" 
                          : "hover:bg-muted"
                      )}
                      data-testid={`merchant-option-${merchant.id}`}
                    >
                      <div className="flex items-center gap-2">
                        <Store className="h-4 w-4" />
                        <div>
                          <div className="text-sm font-medium">{merchant.name}</div>
                          <div className="text-xs text-muted-foreground">{merchant.code}</div>
                        </div>
                      </div>
                      {isSelected && <Check className="h-4 w-4 text-primary" />}
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default MerchantComboSelect;
